import React, { PropsWithChildren, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { QuantityInput, QuantityValidationError } from '@pages/Trade/components/QuantityInput'
import { clampQuantity } from '@pages/Trade/utils/helpers'

import { BaseModalType } from '@context/ModalsContext'

import { SensitiveData } from '@ui/Privacy/SensitiveData'
import { Body2, Title1 } from '@ui/Typography'
import { Modal } from '@ui/components/Modal'

import { CurrencyType } from '@model/WalletDto'

import { useFormatNumber } from '@hooks/useFormatNumber'

import { Box } from '@shared/components/Box'

import { useInstrumentLotMetadata } from '../../hooks/useInstrumentLotMetadata'
import { FIX_POSITION_EFFECT } from '../../utils/constants'
import { getStepDecimals } from '../../utils/helpers'
import { LivePriceCell } from '../components/LivePriceCell'
import { TradePnL } from '../components/TradePnL'
import { useConversionRate } from '../hooks/useConversionRatesStore'
import type { PortfolioTrade } from '../hooks/useDxtradePortfolioTrades'
import type {
  ClosePositionOrderParams,
  UseFixTradingWebSocketResult,
} from '../hooks/useFixTradingWebSocket'
import { formatSymbol, renderCellValue } from '../utils/formatters'
import { sideMapping } from '../utils/mapping'

interface CloseTradeModalProps {
  trade: PortfolioTrade
  accountCurrency?: CurrencyType
  closePosition: UseFixTradingWebSocketResult['closePosition']
  accountCode?: string
}

export const CloseTradeModal: BaseModalType<CloseTradeModalProps> = ({
  close,
  trade,
  accountCurrency,
  closePosition,
  accountCode,
}) => {
  const { t } = useTranslation()
  const { formatNumber } = useFormatNumber()
  const fractionDigits = getStepDecimals(trade.priceIncrement)
  const availableLots = trade.sizeLots ?? 0
  const [lotsValue, setLotsValue] = useState<number>(availableLots)
  const { minLot, maxLot, stepLot, hasLotMetadata } = useInstrumentLotMetadata(
    accountCode,
    trade.symbol,
    availableLots
  )
  const { rate: conversionRate, requiresConversion } = useConversionRate(
    trade.instrumentCurrency,
    accountCurrency
  )

  useEffect(() => {
    setLotsValue((prev) => clampQuantity(prev, minLot, maxLot))
  }, [minLot, maxLot])

  const safeLotsValue = clampQuantity(lotsValue, minLot, maxLot)
  const [isLoading, setIsLoading] = useState(false)
  const [quantityError, setQuantityError] = useState<QuantityValidationError | null>(null)
  const isMountedRef = useRef(true)

  useEffect(() => {
    isMountedRef.current = true
    return () => {
      isMountedRef.current = false
    }
  }, [])

  const handleConfirm = () => {
    const normalizedLots = clampQuantity(lotsValue, minLot, maxLot)
    setLotsValue(normalizedLots)
    const fixSide = sideMapping(trade.side)
    const fixAccount = accountCode ? `default:${accountCode}` : undefined
    if (!fixSide || !fixAccount) return
    if (!trade.lotSize) return
    const stepUnits = stepLot ? stepLot * trade.lotSize : undefined
    const qtyDecimals = getStepDecimals(stepUnits) ?? getStepDecimals(trade.lotSize) ?? 0
    const valueInUnits = Number((normalizedLots * trade.lotSize).toFixed(qtyDecimals))
    const closeOrderParams: ClosePositionOrderParams = {
      account: fixAccount,
      symbol: trade.symbol,
      side: fixSide,
      orderQty: valueInUnits,
      ordType: '1',
      positionEffect: FIX_POSITION_EFFECT.CLOSE,
      positionID: trade.tradeId,
    }
    const requestId = closePosition(closeOrderParams, {
      openPrice: trade.entryPrice,
      currency: accountCurrency,
      conversionRate,
      requiresConversion,
      onSettled: () => {
        if (isMountedRef.current) {
          setIsLoading(false)
          close()
        }
      },
    })
    if (!requestId) return
    setIsLoading(true)
  }
  return (
    <Modal>
      <Modal.Header canClose>{t('Portfolio.Close Trade')}</Modal.Header>
      <Modal.Body>
        <Box fullWidth>
          <Box
            flex
            direction='column'
            px='x16'
            py='x16'
            mb='x16'
            radius='x16'
            border='outline-low-em'
          >
            <Box flex justify='space-between' pb='x8'>
              <Label>{t('Portfolio.Asset')}</Label>
              <Value>{formatSymbol(trade.symbol, trade.description)}</Value>
            </Box>
            <Box flex justify='space-between' pb='x8'>
              <Label>{t('Portfolio.EntryPrice')}</Label>
              <Value>
                <SensitiveData>
                  {trade.entryPrice != null
                    ? formatNumber(trade.entryPrice, {
                        minDecimalPlaces: fractionDigits,
                        maxDecimalPlaces: fractionDigits,
                      })
                    : '-'}
                </SensitiveData>
              </Value>
            </Box>
            <Box flex justify='space-between' pb='x8'>
              <Label>{t('Portfolio.Current price')}</Label>

              <LivePriceCell
                trade={{
                  symbol: trade.symbol,
                  side: trade.side,
                  priceIncrement: trade.priceIncrement,
                }}
              />
            </Box>
            <Box flex justify='space-between'>
              <Label>{t('Portfolio.Size')}</Label>
              <Value>
                <SensitiveData>{renderCellValue(trade.sizeLots)}</SensitiveData>
              </Value>
            </Box>
          </Box>

          <Box
            flex
            direction='column'
            px='x16'
            py='x16'
            fullWidth
            radius='x16'
            border='outline-low-em'
          >
            <Box flex justify='space-between'>
              <Label>{t('Portfolio.P&L')}</Label>
              {trade.lotSize && (
                <TradePnL
                  trade={{
                    ...trade,
                    quantity: safeLotsValue * trade.lotSize,
                  }}
                  accountCurrency={accountCurrency}
                  Typography={Title1}
                />
              )}
            </Box>
          </Box>

          <Box py='x16'>
            <QuantityInput
              value={lotsValue}
              min={minLot}
              max={maxLot}
              step={stepLot}
              onChange={setLotsValue}
              onValidationError={setQuantityError}
              disabled={availableLots === 0 || !hasLotMetadata}
              label={t('Portfolio.Lots')}
            />
          </Box>
        </Box>
      </Modal.Body>
      <Modal.Footer>
        <Modal.Footer.Primary
          onClick={handleConfirm}
          loading={isLoading}
          disabled={!!quantityError}
        >
          {t('Confirm')}
        </Modal.Footer.Primary>
      </Modal.Footer>
    </Modal>
  )
}

const Label: React.FC<PropsWithChildren> = ({ children }) => (
  <Body2 color='text-med-em'>{children}</Body2>
)

const Value: React.FC<PropsWithChildren> = ({ children }) => (
  <Body2 color='text-high-em'>{children}</Body2>
)
